import * as React from 'react';
import { FC, useCallback, useEffect } from 'react';

import { Box, Header, Pressable, Text } from '@rbilabs/universal-components';
import queryString from 'query-string';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';

import { LayoutContainer } from 'components/layout/styled';
import Logo from 'components/logo';
import ConfettiFriesAnimation, {
  useConfettiFriesAnimation,
} from 'components/lottie-animations/confetti-fries';
import {
  useLoyaltyLinkWalmartAccountMutation,
  useLoyaltyWalmartEligibilityLazyQuery,
} from 'generated/graphql-gateway';
import useAuth from 'hooks/auth/use-auth';
import { useNavigation } from 'hooks/navigation/use-navigation';
import useEffectOnce from 'hooks/use-effect-once';
import { useAuthContext } from 'state/auth';
import { ModalAuthScreen } from 'state/auth/types';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { portalToFooter } from 'state/mobile-footer';
import { portalToReplaceHeader } from 'state/mobile-header-nav';
import { routes } from 'utils/routing';

import { StyledActionButton, StyledBox, StyledLogo } from './styled';

export const WalmartLandingPage: FC<React.PropsWithChildren<{}>> = () => {
  const { formatMessage } = useIntl();
  const location = useLocation();
  const { isAuthenticated, user } = useAuthContext();
  const { loyaltyUser } = useLoyaltyUser();
  const { setModalAuthState, closeModalAuth, signOut, setIsReAuthenticating } = useAuth();
  const { navigate } = useNavigation();
  const { triggerAnimation } = useConfettiFriesAnimation();
  const [
    getEligibility,
    { loading: eligibilityLoading, error: eligibilityError, data: eligibilityData, called },
  ] = useLoyaltyWalmartEligibilityLazyQuery();
  const [
    linkWalmartAccount,
    { loading: linkLoading, error: linkError, data: linkData },
  ] = useLoyaltyLinkWalmartAccountMutation();

  const queryParams = queryString.parse(location.search);
  const authzCode = queryParams.authz;

  const signOutGlobally = useCallback(async () => {
    setIsReAuthenticating(true);
    await signOut(true);
  }, [setIsReAuthenticating, signOut]);

  // NOTE: currently there is a known issue with lazyQuery where it refires the query
  // when the component is re-rendered; any subsequent requests with same authz will always fail;
  // TODO: experiment with non-lazy query hook to see if we can avoid unneeded refetch
  const checkEligibility = useCallback(async () => {
    if (
      typeof authzCode === 'string' &&
      authzCode &&
      !called &&
      !eligibilityLoading &&
      !eligibilityData &&
      !eligibilityError
    ) {
      getEligibility({
        variables: {
          authz: authzCode,
        },
      }).then(({ data, error }) => {
        if (error) {
          // TODO BKPE-6734 show eligibility error modal
          return;
        }
        const eligibility = data?.loyaltyGetWalmartEligibility;
        if (eligibility) {
          const { eligible, notEligibleReason } = eligibility;
          if (!eligible) {
            // TODO BKPE-6734 show eligibility error modal
            return alert(notEligibleReason);
          }
        }
      });
    }
  }, [authzCode, called, eligibilityData, eligibilityError, eligibilityLoading, getEligibility]);

  // Get eligibility on page load
  useEffectOnce(() => {
    checkEligibility();
  });

  // Close auth modal when leaving page
  useEffect(() => {
    return () => {
      closeModalAuth();
    };
  }, [closeModalAuth]);

  // Close auth modal once user authenticates
  useEffect(() => {
    if (isAuthenticated) {
      closeModalAuth();
    }
  }, [closeModalAuth, isAuthenticated]);

  if (!authzCode) {
    navigate(routes.base);
    return null;
  }

  const eligibility = eligibilityData?.loyaltyGetWalmartEligibility;
  const showLinkButton =
    eligibility && eligibility.eligible && !linkLoading && !linkData && !linkError;

  const HiddenAppHeader = portalToReplaceHeader(() => null);
  const HiddenAppFooter = portalToFooter(() => {
    return (
      <Box padding="$4">
        <StyledActionButton
          onPress={() => {
            if (!isAuthenticated) {
              // TODO: Consider using the new openSignInModal function here
              setModalAuthState({
                screen: ModalAuthScreen.SIGN_IN,
              });
            }
            navigate(routes.walmartLocation);
          }}
        >
          {formatMessage({ id: 'activate' })}
        </StyledActionButton>
      </Box>
    );
  });

  return (
    <LayoutContainer isFullContainer={true}>
      <ConfettiFriesAnimation />

      <StyledBox>
        <Pressable onPress={() => navigate(routes.base)}>
          <StyledLogo>
            <Logo padding="0" />
          </StyledLogo>
        </Pressable>
        <Header paddingTop="$4">Walmart+</Header>
        <Text>AuthZ: {queryParams.authz || 'Not found'}</Text>
        <Text>Linking with: {user?.details.email} </Text>
        {eligibilityLoading && <Text>Loading...</Text>}
        {eligibility && !eligibility.eligible && (
          <Text>Not eligible: {eligibility.notEligibleReason}</Text>
        )}
        {!eligibility && eligibilityError && (
          <Text>Eligibility error: {eligibilityError.message}</Text>
        )}
        {showLinkButton && (
          <>
            <StyledActionButton
              onPress={async () => {
                if (isAuthenticated) {
                  await signOutGlobally();
                }
                setModalAuthState({
                  screen: ModalAuthScreen.SIGN_IN,
                });
              }}
            >
              Switch Account
            </StyledActionButton>
            <StyledActionButton
              onPress={() => {
                linkWalmartAccount({
                  variables: {
                    input: {
                      accessToken: eligibility.accessToken,
                      refreshToken: eligibility.refreshToken,
                      loyaltyId: loyaltyUser?.id,
                    },
                  },
                })
                  .then(({ data, errors }) => {
                    if (errors) {
                      // TODO BKPE-6939 Create account linkage error modals
                      alert('Failed to link Walmart account');
                    }
                    if (data) {
                      const { success, failureReason } = data.loyaltyLinkWalmartAccount;
                      if (success) {
                        triggerAnimation();
                      } else {
                        // TODO BKPE-6939 Create account linkage error modals
                        alert(failureReason);
                      }
                    }
                  })
                  .catch(() => {
                    // TODO BKPE-6939 Create account linkage error modals
                    alert('Failed to link Walmart account');
                  });
              }}
            >
              Link Account
            </StyledActionButton>
          </>
        )}
        {linkLoading && <Text>Linking account...</Text>}
        {linkError && (
          <>
            <Text>Link Error</Text>
            <pre>{JSON.stringify(linkError, null, 2)}</pre>
          </>
        )}
        {linkData && linkData.loyaltyLinkWalmartAccount.success && <Text>Link success</Text>}
      </StyledBox>
      <HiddenAppHeader />
      <HiddenAppFooter />
    </LayoutContainer>
  );
};
